import { useCallback, useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { Search, RefreshCw, X, Trash2, AlertTriangle } from "lucide-react";
import api, { formatApiErrorDetail } from "../../lib/api";

const humanize = (v) => (v ? String(v).replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "—");

/**
 * Generic admin section — used for bookings, bulk-orders, inbox.
 * Config: { title, path, statuses, extraFilters:[{key,label,options}], columns:[{key,label,render?}], detailFields:[{key,label,render?}] }
 */
export default function AdminSection({ config }) {
  const { refreshOverview } = useOutletContext();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [extraFilters, setExtraFilters] = useState({});
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [noteDraft, setNoteDraft] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status_filter = statusFilter;
      Object.entries(extraFilters).forEach(([k, v]) => { if (v) params[k] = v; });
      if (q.trim()) params.q = q.trim();
      const r = await api.get(`/admin/${config.path}`, { params });
      setItems(r.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  }, [config.path, statusFilter, extraFilters, q]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, status) => {
    try {
      const r = await api.patch(`/admin/${config.path}/${id}/status`, { status });
      setItems((p) => p.map((x) => (x.id === id ? r.data : x)));
      if (selected?.id === id) setSelected(r.data);
      toast.success("Status updated");
      refreshOverview?.();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  const addNote = async () => {
    if (!noteDraft.trim() || !selected) return;
    try {
      const r = await api.patch(`/admin/${config.path}/${selected.id}/notes`, { note: noteDraft.trim() });
      setSelected(r.data);
      setItems((p) => p.map((x) => (x.id === r.data.id ? r.data : x)));
      setNoteDraft("");
      toast.success("Note added");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  const deleteRecord = async () => {
    if (!selected) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/${config.path}/${selected.id}`);
      setItems((p) => p.filter((x) => x.id !== selected.id));
      setConfirmDelete(false);
      setSelected(null);
      toast.success("Record deleted");
      refreshOverview?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setDeleting(false); }
  };

  return (
    <div data-testid={`admin-section-${config.path}`}>
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
        <div>
          <h2 className="font-heading text-2xl tracking-tight text-slate-900">{config.title}</h2>
          <p className="mt-1 text-sm text-slate-500">{items.length} record{items.length === 1 ? "" : "s"}</p>
        </div>
        <button onClick={load} data-testid={`refresh-${config.path}`} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-300 text-slate-700 text-sm font-medium hover:border-[#4A6FA5] hover:text-[#4A6FA5] self-start"><RefreshCw className="w-4 h-4" /> Refresh</button>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-4 mb-4 flex flex-wrap items-center gap-3" data-testid={`filters-${config.path}`}>
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, organization…" data-testid={`search-${config.path}`} className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#4A6FA5]" />
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid={`filter-status-${config.path}`} className="rounded-lg border border-slate-300 px-3 py-2 text-sm">
          <option value="">All statuses</option>
          {config.statuses.map((s) => <option key={s} value={s}>{humanize(s)}</option>)}
        </select>
        {config.extraFilters?.map((f) => (
          <select key={f.key} value={extraFilters[f.key] || ""} onChange={(e) => setExtraFilters((p) => ({ ...p, [f.key]: e.target.value }))} data-testid={`filter-${f.key}-${config.path}`} className="rounded-lg border border-slate-300 px-3 py-2 text-sm">
            <option value="">All {f.label}</option>
            {f.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        ))}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {loading ? <div className="p-12 text-center text-slate-500 text-sm">Loading…</div>
        : items.length === 0 ? <div className="p-12 text-center text-slate-500 text-sm" data-testid={`empty-${config.path}`}>No records yet.</div>
        : (<div className="overflow-x-auto"><table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-[0.14em] text-slate-500">
              <tr>{config.columns.map((c) => <th key={c.key} className="text-left px-4 py-3 font-semibold whitespace-nowrap">{c.label}</th>)}<th className="px-4 py-3"></th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map((it) => (
                <tr key={it.id} className="hover:bg-slate-50/60" data-testid={`row-${config.path}-${it.id}`}>
                  {config.columns.map((c) => (
                    <td key={c.key} className="px-4 py-3 text-slate-800 align-top">{c.render ? c.render(it, { updateStatus }) : humanize(it[c.key])}</td>
                  ))}
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => setSelected(it)} data-testid={`view-${config.path}-${it.id}`} className="text-[#4A6FA5] hover:text-[#3B597B] text-sm font-medium">View</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table></div>)}
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 flex" data-testid={`drawer-${config.path}`}>
          <div className="flex-1 bg-slate-900/30" onClick={() => setSelected(null)} />
          <div className="w-full max-w-md bg-white border-l border-slate-200 overflow-y-auto p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-1">Record</p>
                <h3 className="font-heading text-xl text-slate-900">{selected.first_name} {selected.last_name}</h3>
              </div>
              <button onClick={() => setSelected(null)} className="text-slate-500 hover:text-slate-900" data-testid={`close-drawer-${config.path}`}><X className="w-4 h-4" /></button>
            </div>
            <div className="mb-4">
              <label className="text-xs uppercase tracking-[0.16em] text-slate-500 font-semibold">Status</label>
              <select value={selected.status} onChange={(e) => updateStatus(selected.id, e.target.value)} data-testid={`drawer-status-${config.path}`} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm">
                {config.statuses.map((s) => <option key={s} value={s}>{humanize(s)}</option>)}
              </select>
            </div>
            <dl className="space-y-3 text-sm">
              {config.detailFields.map((f) => (
                <div key={f.key}>
                  <dt className="text-xs uppercase tracking-[0.16em] text-slate-500 font-semibold">{f.label}</dt>
                  <dd className="text-slate-800 mt-0.5">{f.render ? f.render(selected) : (selected[f.key] || "—")}</dd>
                </div>
              ))}
            </dl>
            <div className="mt-6 border-t border-slate-200 pt-4">
              <p className="text-xs uppercase tracking-[0.16em] text-slate-500 font-semibold mb-2">Internal notes</p>
              <div className="space-y-2 max-h-48 overflow-y-auto mb-3">
                {(selected.notes || []).length === 0 && <p className="text-xs text-slate-500">No notes yet.</p>}
                {(selected.notes || []).map((n) => (
                  <div key={n.id} className="rounded-lg bg-[#F8FAFC] border border-slate-200 p-3 text-sm">
                    <p className="text-slate-800 whitespace-pre-wrap">{n.text}</p>
                    <p className="mt-1 text-[11px] text-slate-500">{n.author} · {new Date(n.created_at).toLocaleString()}</p>
                  </div>
                ))}
              </div>
              <textarea value={noteDraft} onChange={(e) => setNoteDraft(e.target.value)} rows={3} data-testid={`note-input-${config.path}`} placeholder="Add an internal note (not visible to visitor)…" className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#4A6FA5]" />
              <button onClick={addNote} disabled={!noteDraft.trim()} data-testid={`note-save-${config.path}`} className="mt-2 inline-flex items-center px-4 py-2 rounded-lg bg-[#4A6FA5] text-white text-sm font-medium hover:bg-[#3B597B] disabled:opacity-50">Save note</button>
            </div>
            <div className="mt-6 border-t border-slate-200 pt-4">
              <p className="text-xs uppercase tracking-[0.16em] text-red-700 font-semibold mb-2">Danger zone</p>
              <button
                onClick={() => setConfirmDelete(true)}
                data-testid={`delete-btn-${config.path}`}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-red-200 text-red-700 text-sm font-medium hover:bg-red-50"
              >
                <Trash2 className="w-4 h-4" /> Delete record permanently
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmDelete && selected && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/50 p-4" data-testid={`confirm-delete-${config.path}`} onClick={() => !deleting && setConfirmDelete(false)}>
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start gap-3 mb-4">
              <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-red-50 text-red-600 shrink-0"><AlertTriangle className="w-5 h-5" /></span>
              <div>
                <h3 className="font-heading text-lg font-semibold text-slate-900">Delete this record permanently?</h3>
                <p className="mt-1 text-sm text-slate-600">This action cannot be undone.</p>
              </div>
            </div>
            <dl className="rounded-lg bg-slate-50 border border-slate-200 p-4 text-sm space-y-1.5 mb-6">
              <div><dt className="inline text-slate-500">Name: </dt><dd className="inline text-slate-900 font-medium">{selected.first_name} {selected.last_name}</dd></div>
              {selected.organization && <div><dt className="inline text-slate-500">Organization: </dt><dd className="inline text-slate-900">{selected.organization}</dd></div>}
              <div><dt className="inline text-slate-500">Email: </dt><dd className="inline text-slate-900">{selected.email}</dd></div>
            </dl>
            <div className="flex justify-end gap-2">
              <button onClick={() => setConfirmDelete(false)} disabled={deleting} data-testid={`cancel-delete-${config.path}`} className="px-4 py-2 rounded-lg border border-slate-300 text-slate-700 text-sm font-medium hover:bg-slate-50">Cancel</button>
              <button onClick={deleteRecord} disabled={deleting} data-testid={`confirm-delete-btn-${config.path}`} className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 disabled:opacity-60 inline-flex items-center gap-2">
                {deleting ? "Deleting…" : (<><Trash2 className="w-4 h-4" /> Delete permanently</>)}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export { humanize };
